import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router';

import { GALLERY_HASH, parseGalleryHash, photoHash } from './galleryUtils';

/**
 * Hash-backed open/close state for the listing photo gallery.
 *
 * The URL hash is the single source of truth: '#gallery' opens the gallery
 * overlay, '#gallery-photo-<n>' additionally opens the carousel at image n.
 * Opening pushes history entries so the browser/mobile back gesture closes
 * the carousel first, then the gallery — closing is always navigate(-1).
 *
 * @param {number} imageCount - Number of default (non-surround) listing images
 * @returns {{
 *   galleryOpen: boolean,
 *   photoIndex: number|null,
 *   openGallery: () => void,
 *   openPhoto: (index: number) => void,
 *   goBack: () => void,
 * }}
 */
const useGalleryHistory = (imageCount) => {
  const location = useLocation();
  const navigate = useNavigate();

  const { galleryOpen, photoIndex } = parseGalleryHash(location.hash, imageCount);

  const openGallery = useCallback(() => navigate(GALLERY_HASH), [navigate]);
  const openPhoto = useCallback((index) => navigate(photoHash(index)), [navigate]);
  const goBack = useCallback(() => navigate(-1), [navigate]);

  return { galleryOpen, photoIndex, openGallery, openPhoto, goBack };
};

export default useGalleryHistory;
